import { paths } from '@/constants/paths';
import { normalizePath } from '@/utils/appMenuFilter';
import { resolveManagedAccountsBasePath } from './resolveManagedAccountsBasePath';

const section = paths.advisor.configuration.managedAccounts;

/** The tab the section opens on, matching the index redirect in the route tree. */
const DEFAULT_TAB_SEGMENT = 'data-feeds';

const SECTION_MARKER = '/configuration/managed-accounts';

/**
 * Routes inside the section that render a page. They sit under the section
 * index but are not separate nav items, so permission grants them only when the
 * section itself is granted, and only by exact match — keep in sync with
 * configurationRoutes.tsx.
 */
export const MANAGED_ACCOUNTS_INTERNAL_ROUTES: ReadonlySet<string> = new Set(
  [
    section.dataFeeds,
    section.accountTags,
    section.assetClassifications,
    section.managedAssets,
    section.dataFeedAccounts,
  ].map(normalizePath),
);

/**
 * Where a Managed Accounts URL with no page behind it should land. The route
 * tree already redirects these to the default tab, but the permission guard
 * runs before routing and would otherwise refuse them outright (IU-311).
 *
 * Returns null for the section index, for a route that does render, and for
 * anything outside the section — so a page the backend withheld still reaches
 * the guard's refusal rather than being redirected around it.
 */
export function resolveManagedAccountsFallback(
  pathname: string,
): string | null {
  const normalized = normalizePath(pathname);
  if (!normalized.includes(SECTION_MARKER)) return null;

  const base = normalizePath(resolveManagedAccountsBasePath(normalized));
  if (normalized === base) return null;
  if (MANAGED_ACCOUNTS_INTERNAL_ROUTES.has(normalized)) return null;

  const fallback = `${base}/${DEFAULT_TAB_SEGMENT}`;
  // Redirecting a path to itself would loop. Reachable for a section outside
  // the routes above, so it is cheaper to rule out here than to rely on them.
  return fallback === normalized ? null : fallback;
}
